import React from "react";
import { motion } from "framer-motion";

// Line-by-line masked reveal.
const RevealLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
      className="block will-change-transform"
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full flex items-end overflow-hidden"
    >
      {/* Background image */}
      <img
        src="https://customer-assets.emergentagent.com/job_music-merch-gold/artifacts/qym4w24g_IMG_1167.jpeg"
        alt="Bob9ine"
        className="absolute inset-0 w-full h-full object-cover scale-105 saturate-[0.3] contrast-[1.08] brightness-[0.75]"
        style={{ objectPosition: "center 30%" }}
      />
      {/* Bottom legibility gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/75 to-transparent" />
      {/* Side gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/75 via-[#050505]/25 to-transparent" />
      {/* Gold tint */}
      <div className="absolute inset-0 mix-blend-overlay bg-[#C5A059]/12" />

      {/* Top meta bar */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-20 md:top-24 inset-x-0 z-10"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between text-[10px] uppercase tracking-[0.4em] text-[#F5F5F0]/50">
          <div className="flex items-center gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059]" />
            <span>Latest · Lover Boy 2 · 2026</span>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <span>EST. 2024</span>
            <span className="h-px w-8 bg-[#F5F5F0]/20" />
            <span>RVA</span>
          </div>
        </div>
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-3 mb-6 flex-wrap"
        >
          <span className="h-px w-10 bg-[#C5A059]" />
          <span className="text-[#C5A059] text-xs uppercase tracking-[0.4em]">
            RVA Artist
          </span>
          <span
            data-testid="b9-badge"
            className="b9-badge-ring inline-flex items-center justify-center px-3 py-1 rounded-full font-display text-xs tracking-tight"
            aria-label="B9"
          >
            <span className="b9-badge text-sm">B9</span>
          </span>
        </motion.div>

        {/* Kinetic wordmark */}
        <h1
          data-testid="hero-title"
          className="font-display uppercase font-bold tracking-tighter text-[#F5F5F0] text-6xl sm:text-7xl md:text-8xl lg:text-[10rem] leading-[0.82]"
        >
          <RevealLine delay={0.1}>
            BOB<span className="text-[#C5A059]">9</span>INE
          </RevealLine>
        </h1>

        <motion.p
          data-testid="hero-bio"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-xl text-base md:text-lg text-[#F5F5F0]/80 leading-relaxed"
        >
          Real name{" "}
          <span className="b9-badge font-semibold">Bobby Pemberton</span> — an
          independent artist out of{" "}
          <span className="b9-badge font-semibold">Richmond, VA</span>. My music
          lives in the <span className="b9-badge font-semibold">feelings</span>{" "}
          and struggles that come with{" "}
          <span className="b9-badge font-semibold">love</span>. Every track is a
          moment, every lyric a memory.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="mt-4 max-w-xl text-sm text-[#F5F5F0]/55 leading-relaxed"
        >
          Latest album <span className="text-[#C5A059]">Lover Boy 2</span> —
          streaming now, limited vinyl pressing available.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#music"
            data-testid="hero-cta-music"
            className="group inline-flex items-center gap-3 border border-[#C5A059] text-[#C5A059] px-7 py-3.5 text-xs uppercase tracking-[0.3em] hover:bg-[#C5A059] hover:text-[#050505] transition-all duration-300"
          >
            Full Catalog
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
              →
            </span>
          </a>
          <a
            href="#collector"
            data-testid="hero-cta-merch"
            className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#F5F5F0]/75 hover:text-[#F5F5F0] transition-colors duration-300 px-2 py-3.5"
          >
            Collector Vinyl
          </a>
        </motion.div>
      </div>

      {/* Chapter counter — right side vertical rail */}
      <motion.div
        initial={{ opacity: 0, x: 10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.9, delay: 0.9 }}
        className="hidden lg:flex absolute top-1/2 -translate-y-1/2 right-8 z-10 flex-col items-end gap-3 text-[10px] uppercase tracking-[0.35em] text-[#F5F5F0]/40"
      >
        <span className="writing-mode-vertical">00 / 04</span>
        <span className="h-16 w-px bg-[#F5F5F0]/20" />
        <span className="text-[#C5A059]">Prologue</span>
      </motion.div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 right-6 md:right-12 z-10 flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-[#F5F5F0]/40">
        <span>Scroll</span>
        <span className="block h-px w-12 bg-[#F5F5F0]/30" />
      </div>
    </section>
  );
}
